import { RotateCcw } from 'lucide-react';
import { useId, useMemo, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  localize,
  type Locale,
  type ToolOptionDefinition,
} from '../../../domain/tools/types';
import { formatNumber } from '../../../lib/format';
import '../workspace.css';

export type WorkspaceOptionValue = string | number | boolean;
export type WorkspaceOptionValues = Readonly<Record<string, WorkspaceOptionValue>>;

export interface OptionsPanelProps {
  readonly definitions: readonly ToolOptionDefinition[];
  readonly values: WorkspaceOptionValues;
  readonly errors?: Readonly<Record<string, string | undefined>>;
  readonly requiredKeys?: readonly string[];
  readonly disabled?: boolean;
  readonly onChange: (key: string, value: WorkspaceOptionValue) => void;
  readonly onReset?: () => void;
}

interface OptionFieldProps {
  readonly definition: ToolOptionDefinition;
  readonly value: WorkspaceOptionValue;
  readonly error?: string;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly locale: Locale;
  readonly fieldId: string;
  readonly onChange: (value: WorkspaceOptionValue) => void;
}

function localeFromLanguage(language: string): Locale {
  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

function describedBy(
  help: string | undefined,
  error: string | undefined,
  helpId: string,
  errorId: string,
): string | undefined {
  const ids = [help ? helpId : undefined, error ? errorId : undefined].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

function OptionField({
  definition,
  value,
  error,
  required,
  disabled,
  locale,
  fieldId,
  onChange,
}: OptionFieldProps) {
  const { t } = useTranslation();
  const label = localize(definition.label, locale);
  const help = definition.help ? localize(definition.help, locale) : undefined;
  const placeholder = definition.placeholder
    ? localize(definition.placeholder, locale)
    : undefined;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const descriptionIds = describedBy(help, error, helpId, errorId);
  const commonProps = {
    id: fieldId,
    name: definition.key,
    disabled,
    required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': descriptionIds,
  } as const;

  const fieldLabel = (
    <label className="workspace-option__label" htmlFor={fieldId}>
      <span>{label}</span>
      {required && <span className="workspace-option__required">{t('workflow.options.required')}</span>}
    </label>
  );

  let control;
  switch (definition.kind) {
    case 'textarea':
      control = (
        <textarea
          {...commonProps}
          rows={5}
          value={typeof value === 'string' ? value : String(value)}
          placeholder={placeholder}
          spellCheck={false}
          dir="auto"
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      );
      break;
    case 'select':
      control = (
        <select
          {...commonProps}
          value={typeof value === 'string' ? value : String(value)}
          onChange={(event) => onChange(event.currentTarget.value)}
        >
          {(definition.choices ?? []).map((choice) => (
            <option key={choice.value} value={choice.value}>
              {localize(choice.label, locale)}
            </option>
          ))}
        </select>
      );
      break;
    case 'toggle':
      control = (
        <label className="workspace-toggle" htmlFor={fieldId}>
          <input
            {...commonProps}
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.currentTarget.checked)}
          />
          <span aria-hidden="true" className="workspace-toggle__track">
            <span />
          </span>
          <span className="workspace-toggle__text">
            <span>
              {label}
              {required && <span className="workspace-option__required"> · {t('workflow.options.required')}</span>}
            </span>
            {help && <small id={helpId}>{help}</small>}
          </span>
        </label>
      );
      break;
    case 'number':
      control = (
        <input
          {...commonProps}
          type="number"
          inputMode="decimal"
          min={definition.min}
          max={definition.max}
          step={definition.step}
          value={typeof value === 'boolean' ? '' : value}
          placeholder={placeholder}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.currentTarget.value;
            onChange(nextValue === '' ? '' : event.currentTarget.valueAsNumber);
          }}
        />
      );
      break;
    case 'range': {
      const parsedValue = typeof value === 'number' ? value : Number(value);
      const numericValue = Number.isFinite(parsedValue)
        ? parsedValue
        : Number(definition.defaultValue);
      control = (
        <div className="workspace-range">
          <input
            {...commonProps}
            type="range"
            min={definition.min}
            max={definition.max}
            step={definition.step}
            value={numericValue}
            aria-valuetext={formatNumber(numericValue, locale)}
            onChange={(event) => onChange(event.currentTarget.valueAsNumber)}
          />
          <output htmlFor={fieldId} dir="ltr">{formatNumber(numericValue, locale)}</output>
        </div>
      );
      break;
    }
    case 'color': {
      const proposedColor = typeof value === 'string' ? value : String(definition.defaultValue);
      const colorValue = /^#[\da-f]{6}$/iu.test(proposedColor)
        ? proposedColor
        : String(definition.defaultValue);
      control = (
        <div className="workspace-color">
          <input
            {...commonProps}
            type="color"
            value={colorValue}
            onChange={(event) => onChange(event.currentTarget.value)}
          />
          <bdi dir="ltr">{colorValue.toUpperCase()}</bdi>
        </div>
      );
      break;
    }
    case 'password':
    case 'page-range':
    case 'text':
      control = (
        <input
          {...commonProps}
          type={definition.kind === 'password' ? 'password' : 'text'}
          value={typeof value === 'string' ? value : String(value)}
          placeholder={placeholder}
          autoComplete={definition.sensitive || definition.kind === 'password' ? 'off' : undefined}
          spellCheck={definition.kind === 'text'}
          dir={definition.kind === 'page-range' ? 'ltr' : 'auto'}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      );
      break;
    default: {
      const exhaustive: never = definition.kind;
      control = exhaustive;
    }
  }

  if (definition.kind === 'toggle') {
    return (
      <div className="workspace-option" data-kind="toggle" data-invalid={Boolean(error) || undefined}>
        {control}
        {error && <p id={errorId} className="workspace-option__error" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="workspace-option" data-kind={definition.kind} data-invalid={Boolean(error) || undefined}>
      {fieldLabel}
      {control}
      {help && <p id={helpId} className="workspace-option__help">{help}</p>}
      {error && <p id={errorId} className="workspace-option__error" role="alert">{error}</p>}
    </div>
  );
}

export function OptionsPanel({
  definitions,
  values,
  errors = {},
  requiredKeys = [],
  disabled = false,
  onChange,
  onReset,
}: OptionsPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage ?? i18n.language);
  const panelId = useId().replaceAll(':', '');
  const required = useMemo(() => new Set(requiredKeys), [requiredKeys]);

  if (definitions.length === 0) {
    return null;
  }

  return (
    <fieldset className="workspace-options" disabled={disabled}>
      <legend>{t('workflow.options.title')}</legend>
      {onReset && (
        <div className="workspace-options__reset">
          <button className="workspace-text-button" type="button" disabled={disabled} onClick={onReset}>
            <RotateCcw size={15} aria-hidden="true" />
            {t('workflow.options.resetAll')}
          </button>
        </div>
      )}
      <div className="workspace-options__fields">
        {definitions.map((definition) => {
          const value = Object.hasOwn(values, definition.key)
            ? values[definition.key]
            : definition.defaultValue;
          return (
            <OptionField
              key={definition.key}
              definition={definition}
              value={value ?? definition.defaultValue}
              error={errors[definition.key]}
              required={required.has(definition.key)}
              disabled={disabled}
              locale={locale}
              fieldId={`${panelId}-${definition.key}`}
              onChange={(nextValue) => onChange(definition.key, nextValue)}
            />
          );
        })}
      </div>
    </fieldset>
  );
}
